import React, { useEffect, useReducer, useState } from 'react';
import { CompletionStatus, FetchType } from '../../../enums';
import { IRatingsProps } from './rating.models';
import { RatingScale } from '../../../models/rating-scale';
import { useAppActions, useAppState } from '../../../overmind';
import { Navigate, useParams } from 'react-router';
import * as store from './rating.store';
import { ReviewResource } from '../../../models/review-resource';
import { ExplainerFocusPanelLayout } from '../../../widgets/layouts/focus-panel-layouts/explainer-focus-panel/explainer-focus-panel.layout';
import { filteredColours } from '../../../library/consts/filtered-colours';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { Anchor, Button, PageHeader, Panel, TextArea, UnorderedList, UnorderedListItem, capitalCase, useMatchScreenWidth } from '@keplerco/core';
import { PagePath } from '../../../navigation/navigation.enums';
import { getSubdomain } from '../../../library/helpers/get-subdomain';
import { AssessmentType } from '../../../enums/assessment-type';
import { Rater } from '../../../components/inputs/raters/rater/rater';
import ratingStyles from './rating.module.css';
import classNames from 'classnames';
import { LearnerSelfReviews } from '../../../models/learner-skill-resource';
import { useAnalysisBasePagePath } from '../use-analysis-base-page-path';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { PeerOverviewResponse } from '../../../models/overmind/assessment-analytics';

function RatingKeyPartial({ ratingScale, dialogContained, isSelf = false }: { ratingScale: RatingScale; dialogContained?: boolean; isSelf?: boolean }): JSX.Element {
  return (
    <div className={classNames(ratingStyles.ratingKey, { [ratingStyles.dialogContained]: dialogContained })}>
      <h3 style={{ textTransform: 'uppercase', color: 'var(--accent-2)', marginBottom: 15 }}>Rating key</h3>

      <UnorderedList>
        {ratingScale.map(rating => {
          const label = isSelf ? rating.selfLabel : rating.peerLabel;
          const description = isSelf ? rating.selfDescription : rating.peerDescription;

          return (
            <UnorderedListItem bulletTop="10px" key={rating.rating}>
              <h6>{label}</h6>
              <small>{description}</small>
            </UnorderedListItem>
          );
        })}
      </UnorderedList>
    </div>
  );
}

export function RatingLayout(props: IRatingsProps): JSX.Element {
  const subdomain = getSubdomain();
  const { ratingSlug } = useParams();
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();

  const actions = useAppActions();
  const { activeAssessmentConfig, forcedAssessments, companyVariables } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const [open, setOpen] = useState<boolean>(false);
  const [errorFields, setErrorFields] = useState<ReviewResource[]>();
  const [overallComment, setOverallComment] = useState<string | undefined>();
  const [queriedSkill, setQueriedSkill] = useState<{ name: string; description: string }>();
  const [assessmentOverview, setAssessmentOverview] = useState<PeerOverviewResponse>();

  async function getAssessmentOverview() {
    const assessmentOverview = await actions.getAssessmentAnalyticsPeerOverview({ assessmentSlug: activeAssessmentConfig!.assessmentSlug!, companySlug: companyVariables.slug! });
    setAssessmentOverview(assessmentOverview);
  }

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: props.path, type: FetchType.PageFetching });

      if (props.type === 'peer') {
        const [ratingScale, teamMembers] = await Promise.all([actions.getCompanyRatingScales(), actions.getTeamReviews()]);
        const teamMember = teamMembers?.find(tempTeamMember => tempTeamMember.userSlug === ratingSlug);
        dispatch({
          type: store.RatingActionTypes.PopulatePeer,
          payload: { basePagePath, forcedAssessmentsLength: forcedAssessments.length, assessmentName: activeAssessmentConfig?.assessmentName ?? 'Assessment', ratingScale, teamMember, peerOnly: activeAssessmentConfig?.selfPeerReviewCompletionStatus === CompletionStatus.NoActionRequired },
        });
      } else if (props.type === 'self') {
        const [ratingScale, learnerSelfReviews] = await Promise.all([actions.getCompanyRatingScales(), actions.getSelfReviews()]);
        dispatch({ type: store.RatingActionTypes.PopulateSelf, payload: { basePagePath, forcedAssessmentsLength: forcedAssessments.length, assessmentName: activeAssessmentConfig?.assessmentName ?? 'Assessment', ratingScale, learnerSelfReviews } });
      } else {
        const [ratingScale, careerPathSkillSet] = await Promise.all([actions.getCompanyRatingScales(), actions.getLearnerCareerPathSkillSet(ratingSlug!)]);
        dispatch({ type: store.RatingActionTypes.PopulateCareerPath, payload: { subdomain, basePagePath, forcedAssessmentsLength: forcedAssessments.length, assessmentName: activeAssessmentConfig?.assessmentName ?? 'Assessment', ratingScale, careerPathSkillSet } });
      }

      actions.stopLoader(props.path);
    }

    getData();
    getAssessmentOverview();
  }, []);

  function onRatingPeerHandler(rating: number, skill: ReviewResource, index: number, parent: ReviewResource[]) {
    dispatch({ type: store.RatingActionTypes.PatchPeerSkill, payload: { skillId: skill.companySkillId, skillLevel: rating, comment: skill.comment } });

    if (errorFields) setErrorFields([...errorFields.filter(s => s.companySkillId !== skill.companySkillId)]);

    if (rating === undefined) {
      return;
    }

    const nextSibling = parent[index + 1];
    if (nextSibling) {
      const currentElement = document.getElementById(`rater-${skill.companySkillId}`);
      window.scrollBy({ behavior: 'smooth', top: currentElement?.clientHeight });
    }
  }

  function onRatingCareerPathHandler(rating: number, skill: ReviewResource, index: number, parent: ReviewResource[]) {
    dispatch({ type: store.RatingActionTypes.PatchCareerPathSkill, payload: { skillId: skill.companySkillId, skillLevel: rating } });

    if (errorFields) setErrorFields([...errorFields.filter(s => s.companySkillId !== skill.companySkillId)]);

    const nextSibling = parent[index + 1];
    if (nextSibling) {
      const currentElement = document.getElementById(`rater-${skill.companySkillId}`);
      window.scrollBy({ behavior: 'smooth', top: currentElement?.clientHeight });
    }
  }

  function onRatingSelfHandler(rating: number, skill: ReviewResource) {
    dispatch({ type: store.RatingActionTypes.PatchSelfSkill, payload: { skillId: skill.companySkillId, skillLevel: rating, yearsSpent: skill.yearsSpent ?? 0, comment: skill.comment } });

    if (errorFields) setErrorFields([...errorFields.filter(s => s.companySkillId !== skill.companySkillId)]);
  }

  const isTablet = useMatchScreenWidth('tablet');

  return (
    <PageLoaderLayer path={props.path}>
      <div className="wrapper pageWrapper">
        <div style={{ marginBottom: 30 }}>
          {isTablet ? (
            <PageHeader breadcrumbs={state.breadcrumbs} title={state.title ?? props.title} subtitle={state.subtitle ?? props.subtitle} type="actions">
              <Anchor onClick={() => setOpen(true)}>view rating key</Anchor>
            </PageHeader>
          ) : (
            <PageHeader breadcrumbs={state.breadcrumbs} title={state.title ?? props.title} subtitle={state.subtitle ?? props.subtitle} />
          )}
        </div>

        <div className={ratingStyles.ratingBody}>
          <div className={ratingStyles.ratingScale}>
            {state.type === 'peer' && (
              <React.Fragment>
                {/* @Jedidja */}
                {activeAssessmentConfig?.assessmentType !== AssessmentType.PeerEndorsement && <Navigate to={PagePath.analysisBase} />}
                {!!state.teamMember && (
                  <React.Fragment>
                    {state.teamMember.reviews.map((review, index, parent) => {
                      const colour = filteredColours[index % filteredColours.length];

                      return (
                        <div key={review.companySkillId} id={`rater-${review.companySkillId}`}>
                          {!!state.ratingScale && (
                            <Rater
                              scale={state.ratingScale}
                              data={{
                                id: review.companySkillId,
                                label: review.skillName,
                                description: review.skillDescription,
                                rating: review.skillLevel,
                              }}
                              colour={colour}
                              fadeOnComplete
                              extraRating={{ label: `Don't know` }}
                              onChange={rating => onRatingPeerHandler(rating, review, index, parent)}
                            >
                              {assessmentOverview?.isSkillsFeedbackEnabled && (
                                <TextArea
                                  style={{ display: 'flex', marginTop: '12px' }}
                                  responsive
                                  label={`Comments ${assessmentOverview?.isAnonymous ? '(anonymous)' : ''}`}
                                  onChange={event =>
                                    dispatch({
                                      type: store.RatingActionTypes.PatchPeerSkill,
                                      payload: {
                                        skillId: review.companySkillId,
                                        skillLevel: review.skillLevel ?? undefined,
                                        comment: event.target.value,
                                      },
                                    })
                                  }
                                  value={review.comment}
                                />
                              )}
                            </Rater>
                          )}

                          {errorFields?.find(i => i.companySkillId === review.companySkillId) && (
                            <div style={{ padding: '0 0' }}>
                              <div className="formErrorMessage">Rating cannot be blank</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {assessmentOverview?.isAssessmentFeedbackEnabled && (
                      <div style={{ display: 'flex', gap: 10, flexDirection: 'column', marginTop: 30 }}>
                        <h3>
                          Provide a comment on {state.teamMember.firstName} {state.teamMember.lastName}'s overall skill assessment
                        </h3>
                        <TextArea responsive label={`Comments ${assessmentOverview?.isAnonymous ? '(anonymous)' : ''}`} value={overallComment} onChange={e => setOverallComment(e.target.value)} />
                      </div>
                    )}
                  </React.Fragment>
                )}

                <footer className={ratingStyles.ratingFooter}>
                  <Anchor
                    arrow
                    reverse
                    block
                    onClick={() => {
                      if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
                        keplerNavigate(basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment);
                      } else {
                        keplerNavigate(basePagePath + PagePath.analysisPeerSkillsAssessment);
                      }
                    }}
                  >
                    Back
                  </Anchor>

                  <Button
                    type={'button'}
                    onClick={async () => {
                      const emptyReviews = state.teamMember?.reviews.filter(r => typeof r.skillLevel === 'undefined' || r.skillLevel === null) ?? [];

                      if (emptyReviews?.length > 0) {
                        const first = emptyReviews[0];
                        const element = document.getElementById(`rater-${first.companySkillId}`);
                        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setErrorFields(emptyReviews);
                      } else {
                        actions.startLoader({ path: props.path, type: FetchType.Sending });

                        if (!!state.teamMember) {
                          await actions.submitTeamMemberReview({ ...state.teamMember, hasCompletedReview: true, comment: overallComment });
                        }

                        if (activeAssessmentConfig?.assessmentSlug) actions.getAssessmentConfig(activeAssessmentConfig?.assessmentSlug);

                        actions.resetAllLoaders();

                        if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
                          keplerNavigate(basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment);
                        } else {
                          keplerNavigate(basePagePath + PagePath.analysisPeerSkillsAssessment);
                        }
                      }
                    }}
                  >
                    Submit Rating
                  </Button>
                </footer>
              </React.Fragment>
            )}

            {state.type === 'career-path' && (
              <React.Fragment>
                {/* @Jedidja */}
                {activeAssessmentConfig?.assessmentType !== AssessmentType.FieldsOfPractice && <Navigate to={PagePath.analysisBase} />}
                {!!state.careerPathSkillSet && (
                  <React.Fragment>
                    {state.careerPathSkillSet.reviews
                      .sort((a, b) => a.skillName.localeCompare(b.skillName))
                      .map((review, index, parent) => {
                        const colour = filteredColours[index % filteredColours.length];

                        return (
                          <div key={review.companySkillId} id={`rater-${review.companySkillId}`}>
                            {!!state.ratingScale && (
                              <Rater
                                scale={state.ratingScale}
                                data={{
                                  id: review.companySkillId,
                                  label: review.skillName,
                                  description: review.skillDescription,
                                  rating: review.skillLevel,
                                }}
                                colour={colour}
                                fadeOnComplete
                                onChange={rating => onRatingCareerPathHandler(rating, review, index, parent)}
                              />
                            )}

                            {errorFields?.find(i => i.companySkillId === review.companySkillId) && (
                              <div style={{ padding: '0 0' }}>
                                <div className="formErrorMessage">Rating cannot be blank</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </React.Fragment>
                )}

                <footer className={ratingStyles.ratingFooter}>
                  <Anchor arrow reverse block onClick={() => keplerNavigate(basePagePath + PagePath.analysisRateYourSkills)}>
                    Back
                  </Anchor>

                  <Button
                    type={'button'}
                    onClick={async () => {
                      // @Jedidja
                      if (!!state.careerPathSkillSet && !!activeAssessmentConfig) {
                        const emptyReviews = state.careerPathSkillSet.reviews.filter(review => typeof review.skillLevel === 'undefined' || review.skillLevel === null) ?? [];

                        if (emptyReviews.length > 0) {
                          const first = emptyReviews[0];
                          const element = document.getElementById(`rater-${first.companySkillId}`);
                          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          setErrorFields(emptyReviews);
                        } else {
                          actions.startLoader({ path: props.path, type: FetchType.Sending });
                          await actions.saveLearnerCareerPathSkillSets(state.careerPathSkillSet);

                          actions.resetAllLoaders();

                          // @Jedidja
                          const response = await actions.getAssessmentConfig(activeAssessmentConfig?.assessmentSlug);
                          if (response?.assessmentResultReady) {
                            await actions.getForcedAssessments();
                            actions.clearAssessmentConfig();
                          }
                          keplerNavigate(basePagePath + PagePath.analysisRateYourSkills);
                        }
                      }
                    }}
                  >
                    Submit Ratings
                  </Button>
                </footer>
              </React.Fragment>
            )}

            {state.type === 'self' && (
              <React.Fragment>
                {/* @Jedidja */}
                {activeAssessmentConfig?.assessmentType !== AssessmentType.PeerEndorsement && <Navigate to={PagePath.analysisBase} />}
                {!!state.learnerSelfReviews && (
                  <React.Fragment>
                    {state.learnerSelfReviews.reviews.map((skill, index) => {
                      const colour = filteredColours[index % filteredColours.length];

                      return (
                        <div key={skill.companySkillId} id={`rater-${skill.companySkillId}`}>
                          {!!state.ratingScale && (
                            <Rater
                              scale={state.ratingScale}
                              data={{
                                id: skill.companySkillId,
                                label: skill.skillName,
                                description: skill.skillDescription,
                                rating: skill.skillLevel,
                              }}
                              colour={colour}
                              extraRating={{
                                count: skill.yearsSpent,
                                label: 'Number of years using this skill',
                                maxValue: 99,
                                onChange: years => {
                                  dispatch({ type: store.RatingActionTypes.PatchSelfSkill, payload: { skillId: skill.companySkillId, skillLevel: skill.skillLevel ?? 0, yearsSpent: years, comment: skill.comment } });
                                },
                              }}
                              onChange={(rating: any) => onRatingSelfHandler(rating, skill)}
                            >
                              {assessmentOverview?.isSkillsFeedbackEnabled && (
                                <div style={{ display: 'flex', marginTop: 5 }}>
                                  <TextArea
                                    responsive
                                    label={`Comments ${assessmentOverview?.isAnonymous ? '(anonymous)' : ''}`}
                                    onChange={event => {
                                      dispatch({
                                        type: store.RatingActionTypes.PatchSelfSkill,
                                        payload: {
                                          skillId: skill.companySkillId,
                                          skillLevel: skill.skillLevel ?? 0,
                                          yearsSpent: skill.yearsSpent,
                                          comment: event.target.value,
                                        },
                                      });
                                    }}
                                    value={skill.comment}
                                  />
                                </div>
                              )}
                            </Rater>
                          )}

                          {errorFields?.find(i => i.companySkillId === skill.companySkillId) && (
                            <div style={{ padding: '0 0' }}>
                              <div className="formErrorMessage">Rating cannot be blank</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {assessmentOverview?.isAssessmentFeedbackEnabled && (
                      <div style={{ display: 'flex', gap: 10, flexDirection: 'column', marginTop: 30 }}>
                        <h3>Provide a comment on your overall skill assessment</h3>
                        <TextArea responsive label={`Comments ${assessmentOverview?.isAnonymous ? '(anonymous)' : ''}`} value={overallComment} onChange={e => setOverallComment(e.target.value)} />
                      </div>
                    )}
                  </React.Fragment>
                )}

                <footer className={ratingStyles.ratingFooter}>
                  <Anchor arrow reverse block onClick={() => keplerNavigate(basePagePath + PagePath.analysisFrequencyOfUse)}>
                    Back
                  </Anchor>

                  <Button
                    type="button"
                    onClick={async () => {
                      // @Jedidja
                      if (!!state.learnerSelfReviews && !!activeAssessmentConfig) {
                        const emptyReviews = state.learnerSelfReviews.reviews.filter(review => !review.skillLevel) ?? [];

                        if (emptyReviews.length > 0) {
                          const first = emptyReviews[0];
                          const element = document.getElementById(`rater-${first.companySkillId}`);
                          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          setErrorFields(emptyReviews);
                        } else {
                          actions.startLoader({ path: props.path, type: FetchType.Sending });

                          const selfReviews: LearnerSelfReviews = {
                            comment: overallComment || '',
                            reviews: state.learnerSelfReviews.reviews,
                          };

                          await actions.submitSelfReviews(selfReviews);
                          await actions.completeSelfReview();

                          // @Jedidja
                          const skillAssessmentConfiguration = await actions.getAssessmentConfig(activeAssessmentConfig?.assessmentSlug);
                          if (skillAssessmentConfiguration?.selfReviewCompletionStatus === CompletionStatus.Completed && skillAssessmentConfiguration?.selfPeerReviewCompletionStatus === CompletionStatus.NoActionRequired) {
                            const completedAssessmentSlug = activeAssessmentConfig?.assessmentSlug;
                            
                            const updatedForcedAssessments = await actions.getForcedAssessments();
                            
                            const remainingForcedAssessments = updatedForcedAssessments.filter(
                              assessment => assessment.assessmentSlug !== completedAssessmentSlug
                            );
                            actions.clearAssessmentConfig();
                            
                            if (remainingForcedAssessments.length > 0) {
                              if (remainingForcedAssessments.length === 1) {
                                keplerNavigate(PagePath.analysisBase + PagePath.analysisAssessment.replace(':assessmentSlug', remainingForcedAssessments[0].assessmentSlug));
                              } else {
                                keplerNavigate(PagePath.analysisBase + PagePath.analysisPendingAssessments);
                              }
                            } else {
                              keplerNavigate(PagePath.yourSkills);
                            }
                            actions.stopLoader(props.path);
                            return;
                          }
                          keplerNavigate(basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills);
                          actions.stopLoader(props.path);
                        }
                      }
                    }}
                  >
                    Submit
                  </Button>
                </footer>
              </React.Fragment>
            )}
          </div>

          {!isTablet && !!state.ratingScale && <RatingKeyPartial ratingScale={state.ratingScale} isSelf={state.type === 'self'} />}
        </div>
      </div>

      <Panel open={open} onClose={() => setOpen(false)}>
        <div className="panelContent" style={{ gridTemplateRows: '1fr auto' }}>
          <div>{!!state.ratingScale && <RatingKeyPartial ratingScale={state.ratingScale} dialogContained isSelf={state.type === 'self'} />}</div>

          <footer className="panelFooter" style={{ justifyContent: 'flex-end' }}>
            <Button filled type="button" onClick={() => setOpen(false)}>
              Got it, Thanks!
            </Button>
          </footer>
        </div>
      </Panel>

      <Panel open={!!queriedSkill} onClose={() => setQueriedSkill(undefined)}>
        {!!queriedSkill && (
          <ExplainerFocusPanelLayout title={capitalCase(queriedSkill.name)} onClose={() => setQueriedSkill(void 0)}>
            {queriedSkill.description}
          </ExplainerFocusPanelLayout>
        )}
      </Panel>
    </PageLoaderLayer>
  );
}
